import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/Input";
import { TextArea } from "@/components/Textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/Label";
import {
  MenuFormulaComponent,
  MenuFormulaComponentFormData,
} from "@/redux/services/menu/menu_formula.type";
import {
  useCreateMenuFormulaComponentMutation,
  useUpdateMenuFormulaComponentMutation,
} from "@/redux/services/menu/menu_formula.api";
import toast from "react-hot-toast";
import { logger } from "@/services/Logger";
import { toastApiError } from "@/lib/apiErrors";

interface ComponentFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  component?: MenuFormulaComponent;
  formulaId: string;
  onSuccess?: () => void;
}

const ComponentFormModal: React.FC<ComponentFormModalProps> = ({
  isOpen,
  onClose,
  component,
  formulaId,
  onSuccess,
}) => {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [quantity, setQuantity] = useState(1);
  const [maxChoiceCount, setMaxChoiceCount] = useState(1);
  const [isRequired, setIsRequired] = useState(true);
  const [sortOrder, setSortOrder] = useState(1);

  const [createComponent, { isLoading: isCreating }] =
    useCreateMenuFormulaComponentMutation();
  const [updateComponent, { isLoading: isUpdating }] =
    useUpdateMenuFormulaComponentMutation();

  const isEditing = !!component;
  const isLoading = isCreating || isUpdating;

  useEffect(() => {
    if (component) {
      setName(component.name);
      setDescription(component.description ?? "");
      setQuantity(component.quantity);
      setMaxChoiceCount(component.maxChoiceCount);
      setIsRequired(component.isRequired);
      setSortOrder(component.sortOrder);
    } else {
      setName("");
      setDescription("");
      setQuantity(1);
      setMaxChoiceCount(1);
      setIsRequired(true);
      setSortOrder(1);
    }
  }, [component, isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!name.trim()) {
      toast.error("Le nom du composant est requis");
      return;
    }

    try {
      if (isEditing && component) {
        await updateComponent({
          id: component.id,
          data: {
            name,
            description: description || undefined,
            quantity,
            maxChoiceCount,
            isRequired,
            sortOrder,
          },
        }).unwrap();
        toast.success("Composant mis à jour avec succès");
      } else {
        const data: MenuFormulaComponentFormData = {
          menuFormula: `/api/menu_formulas/${formulaId}`,
          name,
          description: description || undefined,
          quantity,
          maxChoiceCount,
          isRequired,
          sortOrder,
        };
        await createComponent(data).unwrap();
        toast.success("Composant ajouté avec succès");
      }

      onSuccess?.();
      onClose();
    } catch (error: any) {
      logger.error("ComponentFormModal", "Error saving component:", error);
      toastApiError(error);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>
              {isEditing ? "Modifier le composant" : "Ajouter un composant"}
            </DialogTitle>
          </DialogHeader>

          <div className="py-4 space-y-4">
            <div>
              <Input
                label="Nom"
                type="text"
                id="component-name"
                placeholder="Ex: Une boisson au choix"
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </div>

            <div>
              <TextArea
                label="Description"
                placeholder="Description du composant"
                id="component-description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Input
                  label="Quantité incluse"
                  type="number"
                  id="component-quantity"
                  min={1}
                  value={quantity}
                  onChange={(e) => setQuantity(parseInt(e.target.value) || 1)}
                />
              </div>
              <div>
                <Input
                  label="Choix maximum"
                  type="number"
                  id="component-maxChoiceCount"
                  min={1}
                  value={maxChoiceCount}
                  onChange={(e) =>
                    setMaxChoiceCount(parseInt(e.target.value) || 1)
                  }
                />
              </div>
            </div>

            <div>
              <Input
                label="Position"
                type="number"
                id="component-sortOrder"
                min={1}
                value={sortOrder}
                onChange={(e) => setSortOrder(parseInt(e.target.value) || 1)}
              />
            </div>

            <div className="flex items-center space-x-2">
              <Checkbox
                id="component-isRequired"
                checked={isRequired}
                onCheckedChange={(checked) =>
                  setIsRequired(checked as boolean)
                }
              />
              <Label
                htmlFor="component-isRequired"
                label="Composant obligatoire"
                className="cursor-pointer"
              />
            </div>
          </div>

          <DialogFooter>
            <Button type="submit" isLoading={isLoading}>
              {isEditing ? "Mettre à jour" : "Ajouter"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default ComponentFormModal;
